import { useState, useCallback, useRef, useEffect } from "react";
import { Field, Card } from "./components.js";
import { buildInvNo, splitTax, CURRENCIES, fmtCurrency, getTaxIdLabel } from "./utils.js";
import {
  getAllProducts,
  getCurrentCounter,
  createInvoice
} from "./db/index.js";

// ══════════════════════════════════════════════════════════════════════════════
// SESSION STORAGE OPERATIONS (FOR FREE TIER)
// ══════════════════════════════════════════════════════════════════════════════

const SESSION_STORAGE_KEY = "invoice_gen_products_session";

/**
 * Get products from sessionStorage (FREE tier only)
 * @returns {Array} Array of products
 */
const getProductsFromSession = () => {
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Failed to load products from sessionStorage:", error);
    return [];
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN BILLING COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function Billing({
  G, 
  brand, 
  settings, 
  onGenerate, 
  savedBill, 
  setSavedBill,
  editMode, 
  editingInvoice,
  user // ← NEW: User object with tier info
}) {
  
  // ════════════════════════════════════════════════════════════════════════════
  // STATE
  // ════════════════════════════════════════════════════════════════════════════
  
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [invNoPreview, setInvNoPreview] = useState("...");

  const isFREE = user.tierLevel === 0;

  const currencySymbol = brand.currency === "NONE"
    ? ""
    : brand.currency === "CUSTOM"
      ? (brand.customCurrencySymbol || "?")
      : (CURRENCIES.find(c => c.value === (brand.currency || "NONE"))?.symbol || "");

  const currencyCode = brand.currency || "NONE";

  const fmtAmt = (n) => fmtCurrency(
    n,
    currencySymbol,
    settings.currencyFormat || "symbol-before-space",
    currencyCode,
    settings.amountGrouping || "international",
    settings.amountSeparator || "comma-dot",
    settings.amountDecimals !== false
  );
 
  const [customer, setCustomer] = useState(savedBill?.customer || {
    name: "", phone: "", email: "", gstin: "",
    addressLine1: "", addressLine2: "",
    city: "", state: "", country: "", zip: ""
  });

  const [items, setItems] = useState(savedBill?.items || []);
  const [invDate, setInvDate] = useState(savedBill?.invDate || new Date().toISOString().slice(0, 10));
  const [txType, setTxType] = useState(savedBill?.txType || "intra");
  const [overallDiscount, setOverallDiscount] = useState(savedBill?.overallDiscount || { type: "pct", value: "" });
 
  const [search, setSearch] = useState("");
  const [cartSearch, setCartSearch] = useState("");
  const [addQuantities, setAddQuantities] = useState({});
  const [editingOverallDisc, setEditingOverallDisc] = useState("");
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [undoStack, setUndoStack] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  const [pulseCart, setPulseCart] = useState(false);

  const searchInputRef = useRef(null);

  const isGSTIndia = (brand.taxSystem || "NONE") === "GST_INDIA";

  // ════════════════════════════════════════════════════════════════════════════
  // LOAD PRODUCTS (TIER-BASED)
  // ════════════════════════════════════════════════════════════════════════════

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      if (isFREE) {
        // ════════════════════════════════════════════════════════════════════
        // FREE TIER - Load from sessionStorage
        // ════════════════════════════════════════════════════════════════════
        const sessionProducts = getProductsFromSession();
        setProducts(sessionProducts);
      } else {
        // ════════════════════════════════════════════════════════════════════
        // PAID TIER - Load from database
        // ════════════════════════════════════════════════════════════════════
        const productsFromDB = await getAllProducts();
        setProducts(productsFromDB);
      }
    } catch (error) {
      // ✅ DIFFERENTIATE: Permission errors vs actual failures
      if (error.code === 'PERMISSION_DENIED') {
        console.log('ℹ️ Product access restricted to paid tiers');
      } else {
        console.error("Failed to load products:", error);
      }
      setProducts([]); // Fallback to empty array
    } finally {      setProductsLoading(false);
    }
  }, [isFREE]);

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ════════════════════════════════════════════════════════════════════════════
  // UPDATE INVOICE NUMBER PREVIEW (TIER-BASED)
  // ════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const updateInvoicePreview = async () => {
      if (editMode && editingInvoice) {
        setInvNoPreview(editingInvoice.invNo);
        return;
      }

      if (isFREE) {
        // FREE TIER - Simple preview
        setInvNoPreview(`FREE-${new Date().getTime()}`);
      } else {
        // PAID TIER - Use counter from database
        try {
          const currentCounter = await getCurrentCounter(invDate, settings.invDateFormat);
          const nextCounter = currentCounter + 1;
          const preview = buildInvNo(settings, invDate, nextCounter);
          setInvNoPreview(preview);
                } catch (error) {
          // ✅ DIFFERENTIATE: Permission errors vs actual failures
          if (error.code === 'PERMISSION_DENIED') {
            console.log('ℹ️ Counter access restricted to paid tiers');
            setInvNoPreview("PAID-ONLY");
          } else {
            console.error("Failed to generate invoice preview:", error);
            setInvNoPreview("ERROR");
          }
        }
      }
    };

    updateInvoicePreview();
  }, [editMode, editingInvoice, invDate, settings, isFREE]);

  // ════════════════════════════════════════════════════════════════════════════
  // CAPACITY WARNING (PAID TIER ONLY)
  // ════════════════════════════════════════════════════════════════════════════

  const [capacityWarning, setCapacityWarning] = useState(null);

  useEffect(() => {
    const checkCapacity = async () => {
      if (editMode || isFREE) {
        setCapacityWarning(null);
        return;
      }
     
      try {
        const currentCounter = await getCurrentCounter(invDate, settings.invDateFormat);
        const minDigits = settings.invMinDigits || 4;
        const maxCapacity = Math.pow(10, minDigits) - 1;
        const threshold = Math.floor(maxCapacity * 0.9);
       
        if (currentCounter >= threshold && currentCounter < maxCapacity) {
          setCapacityWarning(
            `⚠️ ${currentCounter} / ${maxCapacity} invoices generated today (${Math.floor((currentCounter/maxCapacity)*100)}% capacity). Counter will auto-expand if needed.`
          );
        } else {
          setCapacityWarning(null);
        }
            } catch (error) {
        // ✅ SILENT: Capacity check is optional, permission errors expected for FREE
        if (error.code !== 'PERMISSION_DENIED') {
          console.error("Failed to check capacity:", error);
        }
        setCapacityWarning(null);
      }
    };

    checkCapacity();
  }, [editMode, invDate, settings.invDateFormat, settings.invMinDigits, isFREE]);

  // ════════════════════════════════════════════════════════════════════════════
  // KEYBOARD SHORTCUTS
  // ════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'SELECT') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
     
      if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setSearch('');
      }
    };
   
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // AUTO-DISMISS TOAST
  // ════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
        setUndoStack(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ════════════════════════════════════════════════════════════════════════════
  // PRODUCT FILTERING & CART OPERATIONS
  // ════════════════════════════════════════════════════════════════════════════

  const filtered = products
    .filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.barcode && p.barcode.includes(search)) ||
      (p.productId && p.productId.toLowerCase().includes(search.toLowerCase())) ||
      (p.hsn && p.hsn.includes(search))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const addItem = useCallback((product) => {
    const raw = addQuantities[product.name];
    const qtyToAdd = raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : 1;
    const safeQty = Math.max(1, Math.min(qtyToAdd, 999));
   
    setItems(prev => {
      const i = prev.findIndex(x => x.name === product.name);
      if (i > -1) return prev.map((x, j) => j === i ? { ...x, qty: x.qty + safeQty } : x);
     
      return [...prev, { ...product, qty: safeQty, itemDiscount: { type: "pct", value: "" } }];
    });
   
    setPulseCart(true);
    setTimeout(() => setPulseCart(false), 400);
   
    setAddQuantities(prev => {
      const next = { ...prev };
      delete next[product.name];
      return next;
    });
  }, [addQuantities]);

  const handleSearchKey = (e) => {
    if (e.key !== "Enter") return;
   
    const trimmed = search.trim();
    if (!trimmed) return;

    const exactBarcodeMatches = products.filter(p => p.barcode === trimmed);

    if (exactBarcodeMatches.length === 1) {
      addItem(exactBarcodeMatches[0]);
      setTimeout(() => setSearch(""), 430);
    } else if (exactBarcodeMatches.length > 1) {
      setDuplicateModal({ products: exactBarcodeMatches });
    } else if (filtered.length > 0) {
      addItem(filtered[0]);
      setTimeout(() => setSearch(""), 430);
    }
  };

  const handleModalSelect = (product) => {
    addItem(product);
    setDuplicateModal(null);
    setTimeout(() => setSearch(""), 430);
  };

  const removeItem = (itemName) => {
    const removed = items.find(x => x.name === itemName);
   
    setUndoStack({ item: removed, timestamp: Date.now() });
    setItems(prev => prev.filter(x => x.name !== itemName));
   
    setToast({
      message: `Removed ${itemName}`,
      action: "Undo"
    });
  };

  const handleUndo = () => {
    if (undoStack?.item) {
      setItems(prev => [...prev, undoStack.item]);
      setToast(null);
      setUndoStack(null);
    }
  };

  const handleClearCustomer = () => {
    setCustomer({
      name: "", phone: "", email: "", gstin: "",
      addressLine1: "", addressLine2: "",
      city: "", state: "", country: "", zip: ""
    });
  };

  const handleClearCart = () => {
    setItems([]);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // INVOICE CALCULATIONS
  // ════════════════════════════════════════════════════════════════════════════

  const lines = items.map(item => {
    const base = item.price * item.qty;
    const disc = item.itemDiscount;
    const discAmt = disc?.value ?
      (disc.type === "pct" ? base * parseFloat(disc.value) / 100 : Math.min(parseFloat(disc.value), base)) : 0;
    const afterDisc = base - discAmt;
    const split = splitTax(item, txType, brand.taxSystem, brand.canadaMode);
    const sgstAmt = afterDisc * split.sgst / 100;
    const cgstAmt = afterDisc * split.cgst / 100;
    const igstAmt = afterDisc * split.igst / 100;
    const otherAmt = afterDisc * split.other / 100;
    const other2Amt = afterDisc * split.other2 / 100;
    const taxTotal = sgstAmt + cgstAmt + igstAmt + otherAmt + other2Amt;
    return {
      ...item, base, discAmt, afterDisc, split,
      sgstAmt, cgstAmt, igstAmt, otherAmt, other2Amt, taxTotal,
      total: afterDisc + taxTotal
    };
  });

  const subtotal = lines.reduce((s, l) => s + l.afterDisc, 0);
  const totalTax = lines.reduce((s, l) => s + l.taxTotal, 0);
  const itemDiscountTotal = lines.reduce((s, l) => s + l.discAmt, 0);
  const preOverallDisc = subtotal + totalTax;
  const overallDiscAmt = overallDiscount.value ?
    (overallDiscount.type === "pct" ?
      preOverallDisc * parseFloat(overallDiscount.value) / 100 :
      Math.min(parseFloat(overallDiscount.value), preOverallDisc)) : 0;
  const grandTotal = preOverallDisc - overallDiscAmt;
  const totalQty = lines.reduce((s, l) => s + l.qty, 0);
  const totalItems = lines.length;
  const canGenerate = customer.name && lines.length > 0;

  // ════════════════════════════════════════════════════════════════════════════
  // INVOICE GENERATION (TIER-BASED)
  // ════════════════════════════════════════════════════════════════════════════

  const handleGenerate = async () => {
    if (!canGenerate) return;
   
    try {
      // Prepare invoice data (common for both tiers)
      const invoiceData = {
        customer,
        lines,
        invDate,
        txType: isGSTIndia ? txType : null,
        subtotal,
        totalTax,
        itemDiscountTotal,
        overallDiscAmt,
        grandTotal,
        overallDiscount
      };

      if (isFREE) {
        // ════════════════════════════════════════════════════════════════════
        // FREE TIER - Create in-memory invoice (no database)
        // ════════════════════════════════════════════════════════════════════
        
        const fakeInvoice = {
          id: `temp_${Date.now()}`,
          invNo: `FREE-${Date.now()}`,
          invDate,
          timestamp: new Date().toLocaleString(),
          customer,
          txType: isGSTIndia ? txType : null,
          lines,
          
          // Embed current brand/settings as snapshots
          brandSnapshot: brand,
          settingsSnapshot: settings,
          
          // Map for compatibility (InvoicePreview expects these)
          seller: brand,
          formatSettings: settings,
          
          // Totals
          subtotal,
          totalTax,
          itemDiscountTotal,
          overallDiscAmt,
          overallDiscount,
          grandTotal,
          
          // Terms
          terms: settings.notesSections || [],
          
          // Calculated fields
          totalItems: lines.length,
          totalQty: lines.reduce((sum, line) => sum + line.qty, 0)
        };
        
        // Clear saved bill
        setSavedBill(null);
        
        // Pass to App.js with FREE flag
        onGenerate(fakeInvoice, { isFREE: true });
        
            } else {
        // ════════════════════════════════════════════════════════════════════
        // PAID TIER - Create in database
        // ════════════════════════════════════════════════════════════════════
        
        try {
          const result = await createInvoice(invoiceData);

          if (!result.success) {
            setAlertModal({
              title: "Failed to Create Invoice",
              message: result.error || "An unknown error occurred. Please try again."
            });
            return;
          }

          // Clear saved bill
          setSavedBill(null);

          // Pass to App.js with PAID flag
          onGenerate(result.invoice, { isFREE: false });
          
        } catch (error) {
          console.error("Error creating invoice:", error);
          
          setAlertModal({
            title: "Failed to Create Invoice",
            message: error.message || "An unexpected error occurred. Please try again."
          });
        }
      }

    } catch (error) {
      console.error("Error generating invoice:", error);
      setAlertModal({
        title: "Error",
        message: error.message || "Failed to generate invoice. Please check console for details."
      });
    }
  };

  const buttonText = editMode ? "Update Invoice ✓" : "Save & Generate Invoice →";

  const getCartQty = (productName) => {
    const cartItem = items.find(x => x.name === productName);
    return cartItem ? cartItem.qty : 0;
  };

  // ════════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ════════════════════════════════════════════════════════════════════════════

  if (!brand || !settings) {
    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        minHeight: "60vh",
        flexDirection: "column",
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⏳</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: G.ink }}>Loading...</div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
     
      {/* Header with Inline Invoice Info */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, gap: 20, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: G.ink, marginTop: 1.9, marginBottom: 4, lineHeight: 1.2 }}>
            {editMode ? "Edit Invoice" : "Create Invoice"}
          </h2>
          <div style={{ color: G.muted, fontSize: 13 }}>
            {editMode ? `Updating ${editingInvoice?.invNo || "invoice"}` : "Press / to search • Enter to add • Esc to clear"}
          </div>
        </div>
       
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-end" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
            <div style={{ flexShrink: 0, minWidth: 130 }}>
              <Field label={editMode ? "Invoice No (Locked)" : "Invoice No (Preview)"}>
                <input
                  value={invNoPreview}
                  readOnly
                  style={{
                    background: G.surface2,
                    cursor: "not-allowed",
                    color: editMode ? G.danger : G.muted,
                    fontWeight: editMode ? 600 : 400,
                    width: "fit-content",
                    minWidth: "auto",
                    maxWidth: "100%"
                  }}
                />
              </Field>
            </div>
            <div style={{ width: 160 }}>
              <Field label="Date">
                <input
                  type="date"
                  value={invDate}
                  onChange={e => setInvDate(e.target.value)}
                  onClick={e => !editMode && e.target.showPicker && e.target.showPicker()}
                  disabled={editMode}
                  style={{
                    background: editMode ? G.surface2 : G.surface,
                    cursor: editMode ? "not-allowed" : "pointer",
                    opacity: editMode ? 0.6 : 1
                  }}
                />
              </Field>
            </div>
          </div>

          {/* GST Toggle */}
          {isGSTIndia && (
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              {[["intra", "Intra-state (SGST+CGST)"], ["inter", "Inter-state (IGST)"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setTxType(val)}
                  style={{
                    flex: 1, padding: "7px 0",
                    border: `1.5px solid ${txType === val ? G.accent : G.border}`,
                    borderRadius: 7,
                    background: txType === val ? G.accentBg : G.surface,
                    color: txType === val ? G.accent : G.muted,
                    fontSize: 12,
                    fontWeight: txType === val ? 600 : 400,
                    cursor: "pointer",
                    fontFamily: "'Sora', sans-serif",
                    transition: "all .15s"
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Duplicate Barcode Modal */}
      {duplicateModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "24px",
            maxWidth: 500, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Multiple Products Found
            </div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 16 }}>
              Multiple products share barcode "{search}". Which one would you like to add?
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {duplicateModal.products.map((p, i) => (
                <button
                  key={i}
                  onClick={() => handleModalSelect(p)}
                  style={{
                    padding: "12px 16px",
                    border: `1.5px solid ${G.border}`,
                    borderRadius: 8,
                    background: G.surface,
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "'Sora', sans-serif",
                    transition: "all .15s"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = G.accent;
                    e.currentTarget.style.background = G.accentBg;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = G.border;
                    e.currentTarget.style.background = G.surface;
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: G.ink }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 12, color: G.muted }}>
                    {fmtAmt(p.price)}
                    {p.productId && ` · ID: ${p.productId}`}
                    {p.hsn && ` · HSN: ${p.hsn}`}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setDuplicateModal(null)}
              style={{
                marginTop: 16,
                width: "100%",
                padding: "10px",
                border: `1px solid ${G.border}`,
                borderRadius: 8,
                background: G.surface2,
                color: G.ink,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 10000
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "24px",
            maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 22 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: G.dangerBg, color: G.danger,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 700, flexShrink: 0
              }}>
                !
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: G.ink, marginBottom: 6 }}>
                  {alertModal.title}
                </div>
                <div style={{ fontSize: 16, color: G.ink2, lineHeight: 1.5 }}>
                  {alertModal.message}
                </div>
              </div>
            </div>
            <button
              onClick={() => setAlertModal(null)}
              style={{
                width: "100%",
                padding: "11px 16px",
                border: "none",
                borderRadius: 7,
                background: G.ink,
                color: G.surface,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 600
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Edit Mode Indicator */}
      {editMode && (
        <div style={{
          background: G.accentBg,
          border: `1px solid ${G.accent}`,
          borderRadius: 8,
          padding: "10px 16px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 10
        }}>
          <span style={{ fontSize: 16 }}>✏️</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: G.accent }}>
              Editing Invoice: {editingInvoice?.invNo}
            </div>
            <div style={{ fontSize: 11, color: G.muted }}>
              Invoice number and date are locked during editing
            </div>
          </div>
        </div>
      )}

      {/* Capacity Warning */}
      {capacityWarning && (
        <div style={{
          background: "#FFF7ED",
          border: "1px solid #FB923C",
          borderRadius: 8,
          padding: "10px 16px",
          marginBottom: 16,
          fontSize: 12,
          color: "#C2410C"
        }}>
          {capacityWarning}
        </div>
      )}

      {/* Customer Info Card */}
      <Card G={G} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{
            fontWeight: 600, fontSize: 12, color: G.muted,
            textTransform: "uppercase", letterSpacing: .5
          }}>
            Customer Info
          </div>
          {(customer.name || customer.phone || customer.email || customer.gstin ||
            customer.addressLine1 || customer.addressLine2 ||
            customer.city || customer.state || customer.country || customer.zip) && (
            <button
              onClick={handleClearCustomer}
              title="Clear customer details"
              style={{
                background: "#fff0f0",
                border: "1.5px solid #fca5a5",
                color: "#dc2626",
                borderRadius: 7,
                padding: "3px 9px",
                fontSize: 14,
                cursor: "pointer",
                transition: "all .2s",
                fontFamily: "'Sora', sans-serif"
              }}
            >
              🗑
            </button>
          )}
        </div>
       
        {/* Row 1: Name & Tax ID */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 3 }}>
          <Field label="Name *">
            <input
              value={customer.name}
              onChange={e => setCustomer(p => ({ ...p, name: e.target.value }))}
              placeholder="Customer Name"
              maxLength={45}
            />
          </Field>
          <Field label="Tax ID">
            <input
              value={customer.gstin}
              onChange={e => setCustomer(p => ({ ...p, gstin: e.target.value }))}
              placeholder={`${getTaxIdLabel(brand.taxSystem) || "Tax ID"}`}
              maxLength={20}
            />
          </Field>
        </div>

        {/* Row 2: Email & Phone */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 3 }}>
          <Field label="Phone">
            <input
              value={customer.phone}
              onChange={e => {
                const value = e.target.value;
                if (/^[0-9+\s\-()]*$/.test(value)) {
                  setCustomer(p => ({ ...p, phone: value }));
                }
              }}
              placeholder="+1 234 567 8901"
              maxLength={20}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={customer.email}
              onChange={e => setCustomer(p => ({ ...p, email: e.target.value }))}
              placeholder="customer@email.com"
              maxLength={50}
            />
          </Field>
        </div>

        {/* Address - Line 1 & 2 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 3 }}>
          <Field label="Address Line 1">
            <input
              value={customer.addressLine1}
              onChange={e => setCustomer(p => ({ ...p, addressLine1: e.target.value }))}
              placeholder="House / Office Number, Building / Apartment, Street"
              maxLength={100}
            />
          </Field>
          <Field label="Address Line 2">
            <input
              value={customer.addressLine2}
              onChange={e => setCustomer(p => ({ ...p, addressLine2: e.target.value }))}
              placeholder="Area / Locality, Landmark"
              maxLength={100}
            />
          </Field>
        </div>

        {/* Address - City, State, ZIP, Country */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <Field label="City">
            <input
              value={customer.city}
              onChange={e => setCustomer(p => ({ ...p, city: e.target.value }))}
              placeholder=""
              maxLength={85}
            />
          </Field>
          <Field label="State / Province">
            <input
              value={customer.state}
              onChange={e => setCustomer(p => ({ ...p, state: e.target.value }))}
              placeholder=""
              maxLength={50}
            />
          </Field>
          <Field label="ZIP / Postal / PIN">
            <input
              value={customer.zip}
              onChange={e => setCustomer(p => ({ ...p, zip: e.target.value }))}
              placeholder=""
              maxLength={12}
            />
          </Field>
          <Field label="Country">
            <input
              value={customer.country}
              onChange={e => setCustomer(p => ({ ...p, country: e.target.value }))}
              placeholder=""
              maxLength={50}
            />
          </Field>
        </div>
      </Card>

      {/* Product Search Card */}
      <Card G={G} style={{ marginBottom: 16 }}>
        <div style={{
          fontWeight: 600, fontSize: 12, color: G.muted,
          textTransform: "uppercase", letterSpacing: .5, marginBottom: 10,
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>🔍</span>
            Search Products
          </div>
          <button
            onClick={loadProducts}
            disabled={productsLoading}
            title={isFREE ? "Refresh products list from browser storage" : "Refresh products list from database"}
            style={{
              background: productsLoading ? G.surface2 : G.accentBg,
              border: `1.5px solid ${productsLoading ? G.border : G.accent}40`,
              color: productsLoading ? G.muted : G.accent,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 500,
              cursor: productsLoading ? "not-allowed" : "pointer",
              fontFamily: "'Sora', sans-serif",
              transition: "all .15s"
            }}
            onMouseEnter={e => {
              if (!productsLoading) {
                e.target.style.background = G.accent;
                e.target.style.color = G.accentFg;
                e.target.style.border = "1.5px solid transparent";
              }
            }}
            onMouseLeave={e => {
              if (!productsLoading) {
                e.target.style.background = G.accentBg;
                e.target.style.color = G.accent;
                e.target.style.border = `1.5px solid ${G.accent}40`;
              }
            }}
          >
            {productsLoading ? "⏳ Loading..." : "🔄 Refresh"}
          </button>
        </div>
       
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <input
            ref={searchInputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleSearchKey}
            placeholder="Search by name, ID, HSN, or scan barcode..."
            style={{ flex: 1 }}
            disabled={productsLoading}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                background: G.accentBg,
                border: `1.8px solid ${G.accent}40`,
                color: G.accent,
                borderRadius: 6,
                padding: "8px 14px",
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 500,
                transition: "all 0.15s",
                whiteSpace: "nowrap"
              }}
              onMouseEnter={e => {
                e.target.style.background = G.accent;
                e.target.style.color = G.accentFg;
                e.target.style.border = "1.8px solid transparent";
              }}
              onMouseLeave={e => {
                e.target.style.background = G.accentBg;
                e.target.style.color = G.accent;
                e.target.style.border = `1.8px solid ${G.accent}40`;
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div style={{ fontSize: 11, color: G.muted, marginBottom: 8 }}>
          {productsLoading 
            ? (isFREE ? "Loading products from browser..." : "Loading products from database...") 
            : `Showing ${filtered.length} of ${products.length} products`}
        </div>

        {/* Product Results */}
        <div
          className="app-scroll"
          style={{
            maxHeight: 400,
            overflowY: "auto",
            border: `1px solid ${G.border}`,
            borderRadius: 8
          }}
        >
          {productsLoading ? (
            <div style={{
              color: G.muted,
              fontSize: 13,
              textAlign: "center",
              padding: "40px 20px",
              background: G.surface
            }}>
              {isFREE ? "Loading products from browser storage..." : "Loading products from database..."}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              color: G.muted,
              fontSize: 13,
              textAlign: "center",
              padding: "40px 20px",
              background: G.surface
            }}>
              {search 
                ? "No products match your search" 
                : products.length === 0 
                  ? "No products in catalog. Add products in Products page." 
                  : "Start typing to search products"}
            </div>
          ) : (
            filtered.map((p, i) => {
              const inCart = getCartQty(p.name);
             
              return (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 14px",
                    borderBottom: i < filtered.length - 1 ? `1px solid ${G.border}` : "none",
                    background: i % 2 === 0 ? G.surface : G.surface2,
                    transition: "all .15s"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = G.accentBg;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = i % 2 === 0 ? G.surface : G.surface2;
                  }}
                >
                  {/* In Cart Badge */}
                  {inCart > 0 && (
                    <div style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: G.accent,
                      color: G.accentFg,
                      borderRadius: 12,
                      padding: "2px 8px",
                      fontSize: 10,
                      fontWeight: 600
                    }}>
                      In cart: {inCart}
                    </div>
                  )}

                  <div style={{ flex: 1, minWidth: 0, paddingRight: inCart > 0 ? 90 : 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: G.ink, marginBottom: 3 }}>
                      {p.productId && <span style={{ color: G.muted, marginRight: 8 }}>{p.productId}</span>}
                      {p.name}
                      <span style={{ color: G.muted, marginLeft: 8, fontWeight: 400 }}>
                        {fmtAmt(p.price)} • {p.tax || 0}% tax
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: G.muted }}>
                      {p.hsn && `HSN: ${p.hsn}`}
                      {p.unit && ` • Unit: ${p.unit}`}
                      {p.barcode && ` • Barcode: ${p.barcode}`}
                      {settings.customField1?.enabled && p.customField1Value &&
                        ` • ${settings.customField1.name}: ${p.customField1Value}`}
                      {settings.customField2?.enabled && p.customField2Value &&
                        ` • ${settings.customField2.name}: ${p.customField2Value}`}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="1"
                      value={addQuantities[p.name] || ""}
                      onChange={e => {
                        let val = e.target.value.replace(/\D/g, "");
                        if (val === "0" || val.startsWith("0")) val = "";
                        if (val.length > 3) val = val.slice(0, 3);
                        setAddQuantities(prev => ({ ...prev, [p.name]: val }));
                      }}
                      style={{
                        width: 54,
                        textAlign: "center",
                        padding: "7px 6px",
                        fontSize: 13,
                        border: `1.5px solid ${G.border}`,
                        borderRadius: 6,
                        background: G.surface,
                        color: G.ink,
                        fontFamily: "'Sora', sans-serif",
                        outline: "none"
                      }}
                      onFocus={e => e.target.style.borderColor = G.accent}
                      onBlur={e => e.target.style.borderColor = G.border}
                    />
                    <button
                      onClick={() => addItem(p)}
                      style={{
                        background: G.accentBg,
                        color: G.accent,
                        border: `1.8px solid ${G.accent}40`,
                        borderRadius: 6,
                        padding: "7px 14px",
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: "pointer",
                        fontFamily: "'Sora', sans-serif",
                        transition: "all .15s",
                        whiteSpace: "nowrap"
                      }}
                      onMouseEnter={e => {
                        e.target.style.background = G.accent;
                        e.target.style.color = G.accentFg;
                        e.target.style.border = "1.8px solid transparent";
                      }}
                      onMouseLeave={e => {
                        e.target.style.background = G.accentBg;
                        e.target.style.color = G.accent;
                        e.target.style.border = `1.8px solid ${G.accent}40`;
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Summary Cards */}
      {lines.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 16
        }}>
          {/* Items Card */}
          <div style={{
            background: "linear-gradient(135deg, #EBF4FF 0%, #D6E9FF 100%)",
            border: `1px solid ${G.accent}33`,
            borderRadius: 10,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 6
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#1E3A8A", textTransform: "uppercase", letterSpacing: 0.5 }}>
              📦 Items
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: G.accent, lineHeight: 1 }}>
              {totalItems}
            </div>
            <div style={{ fontSize: 12, color: "#1E3A8A" }}>
              Product{totalItems !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Quantity Card */}
          <div style={{
            background: "linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)",
            border: "1px solid rgba(168, 85, 247, 0.2)",
            borderRadius: 10,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 6
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6B21A8", textTransform: "uppercase", letterSpacing: 0.5 }}>
              🔢 Quantity
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#A855F7", lineHeight: 1 }}>
              {totalQty}
            </div>
            <div style={{ fontSize: 12, color: "#6B21A8" }}>
              Total units
            </div>
          </div>

          {/* Total Card */}
          <div style={{
            background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
            border: "1px solid rgba(34, 197, 94, 0.2)",
            borderRadius: 10,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 6
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#166534", textTransform: "uppercase", letterSpacing: 0.5 }}>
              💰 Total
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#22C55E", lineHeight: 1 }}>
              {fmtAmt(grandTotal)}
            </div>
            <div style={{ fontSize: 12, color: "#166534" }}>
              Grand total
            </div>
          </div>
        </div>
      )}

      {/* Cart Card */}
      <Card
        G={G}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: pulseCart ? "btnPulse 0.4s ease" : "none"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{
            fontWeight: 600, fontSize: 12, color: G.muted,
            textTransform: "uppercase", letterSpacing: .5,
            display: "flex", alignItems: "center", gap: 6
          }}>
            <span style={{ fontSize: 14 }}>🛒</span>
            Cart {lines.length > 0 && `(${totalItems} item${totalItems !== 1 ? "s" : ""} • ${totalQty} qty)`}
          </div>
          {lines.length > 0 && (
            <button
              onClick={handleClearCart}
              title="Clear cart"
              style={{
                background: "#fff0f0",
                border: "1.5px solid #fca5a5",
                color: "#dc2626",
                borderRadius: 7,
                padding: "4px 10px",
                fontSize: 15,
                cursor: "pointer",
                transition: "all .2s",
                fontFamily: "'Sora', sans-serif"
              }}
            >
              🗑
            </button>
          )}
        </div>
       
        {/* Cart Search */}
        {lines.length > 0 && (
          <input
            value={cartSearch}
            onChange={e => setCartSearch(e.target.value)}
            placeholder="Search cart..."
            style={{
              width: "100%",
              padding: "8px 10px",
              border: `1.5px solid ${G.border}`,
              borderRadius: 7,
              fontSize: 13,
              fontFamily: "'Sora', sans-serif",
              outline: "none",
              color: G.ink,
              background: G.surface,
              marginBottom: 12,
              transition: "border-color 0.15s"
            }}
            onFocus={e => e.target.style.borderColor = G.accent}
            onBlur={e => e.target.style.borderColor = G.border}
          />
        )}
       
        {lines.length === 0 ? (
          <div style={{ textAlign: "center", color: G.muted, padding: "60px 20px", fontSize: 13 }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>🛒</div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Cart is empty</div>
            <div>Search and add products from above</div>
          </div>
        ) : (
          <>
            {/* Cart Table */}
            <div style={{ overflowX: "auto", marginBottom: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: G.ink }}>
                    <th style={{ color: G.surface, padding: "8px", textAlign: "left", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>Product</th>
                    <th style={{ color: G.surface, padding: "8px", textAlign: "right", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>Qty</th>
                    <th style={{ color: G.surface, padding: "8px", textAlign: "right", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>Price</th>
                    <th style={{ color: G.surface, padding: "8px", textAlign: "right", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>Disc%</th>
                    <th style={{ color: G.surface, padding: "8px", textAlign: "right", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>Total</th>
                    <th style={{ color: G.surface, padding: "8px", textAlign: "center", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5, width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines
                    .filter(line => {
                      if (!cartSearch.trim()) return true;
                      const query = cartSearch.toLowerCase();
                      return (
                        line.name.toLowerCase().includes(query) ||
                        (line.productId && line.productId.toLowerCase().includes(query)) ||
                        (line.hsn && line.hsn.includes(query))
                      );
                    })
                    .map((line, i) => (
                      <tr
                        key={i}
                        style={{
                          borderTop: `1px solid ${G.border}`,
                          background: i % 2 === 0 ? G.surface : G.surface2
                        }}
                      >
                        <td style={{ padding: "10px 8px" }}>
                          <div style={{ fontWeight: 500, marginBottom: 2 }}>{line.name}</div>
                          {(line.productId || line.hsn) && (
                            <div style={{ fontSize: 11, color: G.muted }}>
                              {line.productId && `ID: ${line.productId}`}
                              {line.productId && line.hsn && " • "}
                              {line.hsn && `HSN: ${line.hsn}`}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "right" }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={line.qty}
                            onChange={e => {
                              let val = e.target.value.replace(/\D/g, "");
                              if (val === "0" || val === "") val = "1";
                              if (val.length > 3) val = val.slice(0, 3);
                              const newQty = parseInt(val, 10);
                              setItems(prev =>
                                prev.map(x => x.name === line.name ? { ...x, qty: newQty } : x)
                              );
                            }}
                            style={{
                              width: 50,
                              textAlign: "center",
                              padding: "4px",
                              border: `1px solid ${G.border}`,
                              borderRadius: 4,
                              fontSize: 13,
                              fontFamily: "'Sora', sans-serif"
                            }}
                          />
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "right" }}>{fmtAmt(line.price)}</td>
                        <td style={{ padding: "10px 8px", textAlign: "right" }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={line.itemDiscount?.value || ""}
                            placeholder="0"
                            onChange={e => {
                              let val = e.target.value.replace(/[^\d.]/g, "");
                              if (val === '.') val = '0.';
                              const parts = val.split('.');
                              if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                              if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
                              
                              setItems(prev =>
                                prev.map(x =>
                                  x.name === line.name
                                    ? { ...x, itemDiscount: { type: "pct", value: val } }
                                    : x
                                )
                              );
                            }}
                            onBlur={e => {
                              const raw = parseFloat(e.target.value || "0");
                              const clamped = Math.max(0, Math.min(raw, 100));
                              setItems(prev =>
                                prev.map(x =>
                                  x.name === line.name
                                    ? { ...x, itemDiscount: { type: "pct", value: String(clamped) } }
                                    : x
                                )
                              );
                            }}
                            style={{
                              width: 50,
                              textAlign: "center",
                              padding: "4px",
                              border: `1px solid ${G.border}`,
                              borderRadius: 4,
                              fontSize: 13,
                              fontFamily: "'Sora', sans-serif"
                            }}
                          />
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontWeight: 600 }}>
                          {fmtAmt(line.total)}
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "center" }}>
                          <button
                            onClick={() => removeItem(line.name)}
                            style={{
                              background: G.dangerBg,
                              color: G.danger,
                              border: "none",
                              borderRadius: 4,
                              padding: "4px 8px",
                              fontSize: 12,
                              cursor: "pointer",
                              fontFamily: "'Sora', sans-serif"
                            }}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Summary Section */}
            <div style={{ paddingTop: 12, borderTop: `1px solid ${G.border}` }}>
              {itemDiscountTotal > 0 && (
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: G.success,
                  fontSize: 13,
                  marginBottom: 4
                }}>
                  <span>Item discounts</span>
                  <span>−{fmtAmt(itemDiscountTotal)}</span>
                </div>
              )}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                color: G.muted,
                fontSize: 13,
                marginBottom: 4
              }}>
                <span>Subtotal</span>
                <span>{fmtAmt(subtotal)}</span>
              </div>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                color: G.muted,
                fontSize: 13,
                marginBottom: 12
              }}>
                <span>Tax</span>
                <span>{fmtAmt(totalTax)}</span>
              </div>

              {/* Overall Discount */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: G.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>
                  Overall % Discount
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editingOverallDisc !== undefined && editingOverallDisc !== "" ? editingOverallDisc : (overallDiscount.value || "")}
                    placeholder="0.00"
                    onChange={e => {
                      let val = e.target.value.replace(/[^\d.]/g, "");
                      if (val === '.') val = '0.';
                      const parts = val.split('.');
                      if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                      if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
                      setEditingOverallDisc(val);
                    }}
                    onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                    onBlur={() => {
                      const raw = parseFloat(editingOverallDisc || "0");
                      const clamped = Math.max(0, Math.min(raw, 100));
                      setOverallDiscount(p => ({ ...p, value: String(clamped), type: "pct" }));
                      setEditingOverallDisc("");
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 10px",
                      fontSize: 13,
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 6,
                      background: G.surface,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      outline: "none"
                    }}
                    onFocus={e => e.target.style.borderColor = G.accent}
                  />
                  {overallDiscAmt > 0 && (
                    <span style={{ fontSize: 13, color: G.success, fontWeight: 500 }}>
                      = −{fmtAmt(overallDiscAmt)} ✓
                    </span>
                  )}
                </div>
              </div>

              {/* Grand Total */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 700,
                fontSize: 18,
                borderTop: `2px solid ${G.ink}`,
                paddingTop: 12,
                marginBottom: 14
              }}>
                <span>Grand Total</span>
                <span style={{ color: G.accent }}>{fmtAmt(grandTotal)}</span>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={!canGenerate}
                style={{
                  width: "100%",
                  background: canGenerate ? (editMode ? G.gold : G.accent) : G.surface2,
                  color: canGenerate ? G.accentFg : G.muted,
                  border: "none",
                  borderRadius: 7,
                  padding: "12px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: canGenerate ? "pointer" : "not-allowed",
                  fontFamily: "'Sora', sans-serif",
                  transition: "all .15s"
                }}
                onMouseEnter={e => {
                  if (canGenerate) {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                  }
                }}
                onMouseLeave={e => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                {buttonText}
              </button>
             
              {!canGenerate && (
                <div style={{ fontSize: 11, color: G.muted, marginTop: 8, textAlign: "center" }}>
                  Add customer name + at least one product
                </div>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          background: G.ink,
          color: G.surface,
          padding: "12px 16px",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          zIndex: 9999
        }}>
          <span style={{ fontSize: 13 }}>✓ {toast.message}</span>
          {toast.action && (
            <button
              onClick={handleUndo}
              style={{
                background: G.accent,
                color: G.accentFg,
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif"
              }}
            >
              {toast.action}
            </button>
          )}
        </div>
      )}
    </div>
  );
}